/**
 * PlatformSettingsPage
 *
 * Admin page for configuring platform fees and payout settings.
 */

import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';
import { functions } from '@/services/firebase';
import { AdminLayout } from '../layout';
import { usePlatformConfig } from './hooks';
import type { PlatformConfig } from './hooks';
import { FeeCalculatorPreview, ConfigHistory, ConfirmChangeModal } from './components';

export const PlatformSettingsPage = () => {
  const { config, isLoading, updateConfig } = usePlatformConfig();
  const [editedConfig, setEditedConfig] = useState<PlatformConfig | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'fees' | 'payouts' | 'history'>('fees');
  const [evalRunning, setEvalRunning] = useState(false);

  const runSuperhostEval = async () => {
    setEvalRunning(true);
    try {
      const fn = httpsCallable<
        Record<string, never>,
        { hosts: number; awarded: number; revoked: number }
      >(functions, 'runSuperhostEvaluation');
      const { data } = await fn({});
      toast.success(
        `Evaluated ${data.hosts} hosts · +${data.awarded} awarded · -${data.revoked} revoked`
      );
    } catch (err: any) {
      toast.error(err?.message || 'Evaluation failed');
    } finally {
      setEvalRunning(false);
    }
  };

  useEffect(() => {
    if (config) {
      setEditedConfig({ ...config });
    }
  }, [config]);

  const handleSave = async (reason: string) => {
    if (!editedConfig) return;
    await updateConfig(editedConfig, reason);
    setShowConfirmModal(false);
  };

  const hasChanges =
    config && editedConfig && JSON.stringify(config) !== JSON.stringify(editedConfig);

  if (isLoading || !editedConfig) {
    return (
      <AdminLayout>
        <div className="max-w-4xl animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="h-64 bg-gray-200 rounded-xl" />
          <div className="h-48 bg-gray-200 rounded-xl" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Platform Settings</h1>
            <p className="text-gray-500 mt-1">Configure fees, payouts, and platform defaults</p>
          </div>
          {hasChanges && (
            <button
              onClick={() => setShowConfirmModal(true)}
              className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-medium"
            >
              Save Changes
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-lg w-fit">
          {(['fees', 'payouts', 'history'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors capitalize ${
                activeTab === tab
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab === 'fees' ? 'Fee Configuration' : tab === 'payouts' ? 'Payout Settings' : 'Change History'}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'fees' && (
            <>
              {/* Platform Commission */}
              <section className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="font-semibold text-lg mb-1">
                  Platform Commission ({editedConfig.fees.platformCommission.value}%)
                </h2>
                <p className="text-sm text-gray-500 mb-4">
                  Percentage taken on every booking. Choose who pays it: the host
                  (deducted from their wallet credit), the guest (added on top of
                  the listing price), or split 50/50 between both.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Commission Percentage (%)
                    </label>
                    <input
                      type="number"
                      value={editedConfig.fees.platformCommission.value}
                      onChange={(e) =>
                        setEditedConfig({
                          ...editedConfig,
                          fees: {
                            ...editedConfig.fees,
                            platformCommission: {
                              ...editedConfig.fees.platformCommission,
                              value: parseFloat(e.target.value) || 0,
                            },
                          },
                        })
                      }
                      min="0"
                      max="50"
                      step="0.1"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Who pays the commission
                    </label>
                    <select
                      value={editedConfig.fees.platformCommission.mode || 'host_pays'}
                      onChange={(e) =>
                        setEditedConfig({
                          ...editedConfig,
                          fees: {
                            ...editedConfig.fees,
                            platformCommission: {
                              ...editedConfig.fees.platformCommission,
                              mode: e.target.value as 'host_pays' | 'guest_pays' | 'split',
                            },
                          },
                        })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="host_pays">Host pays (deducted from payout)</option>
                      <option value="guest_pays">Guest pays (added as service fee)</option>
                      <option value="split">Split 50/50 between guest and host</option>
                    </select>
                  </div>
                </div>

                {/* Live explanation of the chosen mode */}
                <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700">
                  {(() => {
                    const r = editedConfig.fees.platformCommission.value;
                    const mode = editedConfig.fees.platformCommission.mode || 'host_pays';
                    if (mode === 'host_pays') {
                      return `On a $100 stay: guest pays $100, platform takes $${r.toFixed(0)}, host's wallet credits $${(100 - r).toFixed(0)}.`;
                    } else if (mode === 'guest_pays') {
                      return `On a $100 stay: guest pays $${(100 + r).toFixed(0)} (incl. $${r.toFixed(0)} service fee), platform takes $${r.toFixed(0)}, host's wallet credits $100.`;
                    } else {
                      return `On a $100 stay: guest pays $${(100 + r / 2).toFixed(2)}, platform takes $${r.toFixed(0)}, host's wallet credits $${(100 - r / 2).toFixed(2)}.`;
                    }
                  })()}
                </div>
              </section>

              {/* Fee Calculator Preview */}
              <section className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="font-semibold text-lg mb-4">Fee Calculator Preview</h2>
                <FeeCalculatorPreview config={editedConfig} />
              </section>
            </>
          )}

          {activeTab === 'payouts' && (
            <>
              {/* Payout Thresholds */}
              <section className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="font-semibold text-lg mb-1">Payout Thresholds</h2>
                <p className="text-sm text-gray-500 mb-4">
                  Minimum amounts required for hosts to request payouts
                </p>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Minimum Payout Amount ($)
                  </label>
                  <input
                    type="number"
                    value={editedConfig.payouts.minimumThreshold}
                    onChange={(e) =>
                      setEditedConfig({
                        ...editedConfig,
                        payouts: {
                          ...editedConfig.payouts,
                          minimumThreshold: parseFloat(e.target.value) || 0,
                        },
                      })
                    }
                    min="0"
                    step="10"
                    className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Hosts must have at least this amount available before they can request a payout
                  </p>
                </div>
              </section>

              {/* Transfer Fees */}
              <section className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="font-semibold text-lg mb-1">Transfer Fees</h2>
                <p className="text-sm text-gray-500 mb-4">
                  Fees charged to hosts for different payout methods
                </p>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Wire Transfer Fee ($)
                    </label>
                    <input
                      type="number"
                      value={editedConfig.payouts.wireTransferFee}
                      onChange={(e) =>
                        setEditedConfig({
                          ...editedConfig,
                          payouts: {
                            ...editedConfig.payouts,
                            wireTransferFee: parseFloat(e.target.value) || 0,
                          },
                        })
                      }
                      min="0"
                      step="0.5"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Fee for bank wire transfers (IBAN)
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Whish Money Fee ($)
                    </label>
                    <input
                      type="number"
                      value={editedConfig.payouts.whishMoneyFee}
                      onChange={(e) =>
                        setEditedConfig({
                          ...editedConfig,
                          payouts: {
                            ...editedConfig.payouts,
                            whishMoneyFee: parseFloat(e.target.value) || 0,
                          },
                        })
                      }
                      min="0"
                      step="0.5"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Fee for Whish Money mobile transfers
                    </p>
                  </div>
                </div>
              </section>

              {/* Guest Payments */}
              <section className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="font-semibold text-lg mb-1">Guest Payments</h2>
                <p className="text-sm text-gray-500 mb-4">
                  How guests can pay for bookings
                </p>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editedConfig.payments?.whishPayEnabled === true}
                    onChange={(e) =>
                      setEditedConfig({
                        ...editedConfig,
                        payments: {
                          ...(editedConfig.payments || { whishPayEnabled: false }),
                          whishPayEnabled: e.target.checked,
                        },
                      })
                    }
                    className="mt-1 w-4 h-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-900">
                      Enable Whish Pay hosted payments
                    </span>
                    <span className="block text-xs text-gray-500 mt-1">
                      Guests are redirected to the Whish payment page and the
                      booking is confirmed automatically. Requires the
                      WHISH_CHANNEL / WHISH_SECRET / WHISH_WEBSITE_URL secrets
                      to be set on Cloud Functions. When off, guests follow
                      manual transfer instructions and an admin verifies the
                      payment.
                    </span>
                  </span>
                </label>
              </section>

              {/* Platform Defaults */}
              <section className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="font-semibold text-lg mb-1">Platform Defaults</h2>
                <p className="text-sm text-gray-500 mb-4">
                  Default settings for the platform
                </p>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Default Currency
                    </label>
                    <select
                      value={editedConfig.defaults.currency}
                      onChange={(e) =>
                        setEditedConfig({
                          ...editedConfig,
                          defaults: {
                            ...editedConfig.defaults,
                            currency: e.target.value,
                          },
                        })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="USD">USD - US Dollar</option>
                      <option value="LBP">LBP - Lebanese Pound</option>
                      <option value="EUR">EUR - Euro</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Default Cancellation Policy
                    </label>
                    <select
                      value={editedConfig.defaults.cancellationPolicyId}
                      onChange={(e) =>
                        setEditedConfig({
                          ...editedConfig,
                          defaults: {
                            ...editedConfig.defaults,
                            cancellationPolicyId: e.target.value,
                          },
                        })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="flexible">Flexible</option>
                      <option value="moderate">Moderate</option>
                      <option value="strict">Strict</option>
                      <option value="super_strict">Super Strict</option>
                    </select>
                  </div>
                </div>
              </section>

              {/* Summary Card */}
              <section className="bg-primary-50 rounded-xl p-6">
                <h3 className="font-semibold text-primary-900 mb-3">Current Payout Settings</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-primary-600">
                      ${editedConfig.payouts.minimumThreshold}
                    </p>
                    <p className="text-sm text-gray-600">Min Payout</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-primary-600">
                      ${editedConfig.payouts.wireTransferFee}
                    </p>
                    <p className="text-sm text-gray-600">Wire Fee</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-primary-600">
                      ${editedConfig.payouts.whishMoneyFee}
                    </p>
                    <p className="text-sm text-gray-600">Whish Fee</p>
                  </div>
                </div>
              </section>

              {/* Superhost evaluation */}
              <section className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="font-semibold text-lg mb-1">Superhosts</h2>
                <p className="text-sm text-gray-500 mb-4">
                  Recomputed automatically every day. Criteria: rating 4.8+ across
                  3+ reviews, 5+ completed stays, under 5% host cancellations.
                </p>
                <button
                  type="button"
                  onClick={runSuperhostEval}
                  disabled={evalRunning}
                  className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  {evalRunning ? 'Evaluating…' : 'Run evaluation now'}
                </button>
              </section>
            </>
          )}

          {activeTab === 'history' && (
            <section className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="font-semibold text-lg mb-4">Configuration Change History</h2>
              <ConfigHistory />
            </section>
          )}
        </div>

        {/* Floating Save Button */}
        {hasChanges && (
          <div className="fixed bottom-6 right-6">
            <button
              onClick={() => setShowConfirmModal(true)}
              className="px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-medium shadow-lg flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              Save Changes
            </button>
          </div>
        )}

        {/* Confirm Modal */}
        <ConfirmChangeModal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          currentConfig={config}
          newConfig={editedConfig}
          onConfirm={handleSave}
        />
      </div>
    </AdminLayout>
  );
};

export default PlatformSettingsPage;
